// Bridges Vuex state to the React micro-frontend, mirroring the HostBridge contract
// defined in tf-frontend/src/hostBridge/types.ts. This is the single seam between
// the two frameworks - keep it small and keep the outbound side an explicit allowlist.

const STATUS_COLOR_KEYS = ["matched", "review", "shortlist", "interview", "offered"];

// The candidates loaded in the list a candidate was opened from, in list order - the same two
// sources the Vue candidate page's previous / next use (getPreviousCandidateId /
// getNextCandidateId in modules/candidateList.js). The React candidate page steps through these.
function selectCandidateNavUids(getters) {
  const page = getters.currentNavigationCandidatesPage;
  const rows =
    page && page.currentPage === "Job Candidates Status"
      ? (getters.getJobCandidatesByStatusID || []).map((row) => row.candidate)
      : getters.getCandidates || [];
  return rows.map((c) => c && c.candidate_uid).filter((uid) => uid != null);
}

function selectHostState(store) {
  const { getters, state } = store;
  return {
    isAuthenticated: !!(getters.getUserData && getters.getUserData.access_token),
    accessToken: (getters.getUserData && getters.getUserData.access_token) || null,
    userId: getters.getUserId ?? null,
    customerId: getters.getCustomerId ?? null,
    orgId: getters.getOrgIDFromAccessToken ?? null,
    isChildUser: !!getters.getIsChildUser,
    userName: getters.getUserName || "",
    isLoading: !!getters.isLoading,
    role: getters.getRole || "",
    statusColors: STATUS_COLOR_KEYS.reduce((acc, key) => {
      acc[key] = state[key];
      return acc;
    }, {}),
    candidateNavUids: selectCandidateNavUids(getters),
  };
}

function shallowEqualHostState(a, b) {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => {
    if (key === "statusColors") return JSON.stringify(a[key]) === JSON.stringify(b[key]);
    // Rebuilt on every read, so compare the ids, not the array.
    if (key === "candidateNavUids") return a[key].join(",") === b[key].join(",");
    return a[key] === b[key];
  });
}

// Closed allowlist: the sub-app can never trigger an arbitrary Vuex action or route.
function makeDispatch(store, router, syncState) {
  const handlers = {
    "host/showLoader": () => store.dispatch("showLoader"),
    "host/hideLoader": () => store.dispatch("hideLoader"),
    "host/triggerLoader": (payload) => store.dispatch("triggerLoader", !!payload),
    // Sub-app's JWT expired (API returned 403). refreshToken re-hits /login/refresh-token
    // and commits LOGIN_SUCCESS with the new tokens; the store.watch below picks up the
    // changed access_token and pushes it back to the sub-app automatically.
    "host/refreshToken": () => store.dispatch("refreshToken"),
    // Sub-app just finished candidate registration and wants the new account signed in
    // immediately instead of landing on the login form. This is the same `login` action a
    // manual login uses, so it commits LOGIN_SUCCESS (picked up by store.watch below, same as
    // refreshToken) and pushes the candidate to /react/dashboard itself on success.
    "host/login": (payload) => {
      if (payload && payload.username && payload.password) {
        // isAutoLoginAfterSignup (not is_registration_page - that flag skips the post-login
        // dashboard redirect, which this flow still needs) makes the login action's catch
        // block show a toast, drop the host/showLoader overlay from outbound.ts, and send the
        // candidate to the login page on any failure, not just 400/401.
        store.dispatch("login", {
          username: payload.username,
          password: payload.password,
          isAutoLoginAfterSignup: true,
        });
      }
    },
    "host/navigate": (payload) => {
      if (typeof payload === "string" && payload.startsWith("/")) {
        // Record this as react-originated BEFORE pushing: the view's beforeRouteUpdate
        // guard fires synchronously as part of router.push resolving, so it must already
        // see the flag by the time it runs.
        syncState.lastReactNavigatedPath = payload;
        router.push(payload).catch(() => {});
      }
    },
    // Sub-app just uploaded a new profile photo. The header avatar (TheHeaderDropdownAccnt.vue)
    // reads its own Vuex state, populated once via getUserMe -> fetchMyUploadedDocument, which
    // the sub-app never touches - so it stays stale until this re-fetches it, the same action
    // every other part of this app already calls after login/navigation.
    "host/refreshUserMe": () => store.dispatch("getUserMe", { customer: store.getters.getCustomerId }),
    // Sub-app's Delete My Account succeeded: the backend has deactivated the login, so end the
    // session the same way the header's Logout does.
    "host/logout": () => store.dispatch("logout"),
    // Admin DB switch (modules/dbSwitch.js): the sub-app's API call answered 401 DB_SWITCHED /
    // 503 MAINTENANCE. The same handling as this app's own calls (helpers/interceptors.js), so
    // there's one overlay and one logout for both apps.
    "host/sessionEnded": () => {
      if (!store.getters.isOwnDbSwitchRunning) store.dispatch("dbSwitchedLogout");
    },
    "host/maintenance": () => store.dispatch("startDbSwitchMaintenance"),
    // The React candidate page's "next" reached the last loaded candidate: load the list's next
    // page, as the Vue candidate page does. It stops by itself once the list has no more.
    "host/loadMoreCandidates": () => store.dispatch("fetchMoreCandidateList"),
  };

  return (action) => {
    const handler = action && handlers[action.type];
    if (!handler) {
      console.warn("[qiankun/hostBridge] rejected unknown action", action);
      return;
    }
    handler(action.payload);
  };
}

export function createHostBridge(store, router) {
  let snapshot = selectHostState(store);
  const listeners = new Set();
  // React's own memory router owns page-to-page navigation; when it moves, it asks us
  // (host/navigate) to mirror the URL via Vue Router. That mirroring push matches the same
  // wildcard route record React is already mounted under, so ReactAppHost's
  // beforeRouteUpdate guard would otherwise treat it as an external navigation and remount
  // the whole micro-app just to reflect a URL it already knows about. This lets that guard
  // tell the two apart.
  const syncState = { lastReactNavigatedPath: null };

  const unwatch = store.watch(
    () => selectHostState(store),
    (next) => {
      if (shallowEqualHostState(next, snapshot)) return;
      snapshot = next;
      listeners.forEach((listener) => {
        try {
          listener(next);
        } catch (err) {
          console.error("[qiankun/hostBridge] listener error", err);
        }
      });
    },
    { deep: false },
  );

  return {
    version: 1,
    mode: "qiankun",
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispatch: makeDispatch(store, router, syncState),
    isReactOriginatedPath(path) {
      return syncState.lastReactNavigatedPath === path;
    },
    destroy() {
      unwatch();
      listeners.clear();
    },
  };
}
